/**
 * CalendarDayModal - Day Events Sheet
 *
 * Shows events for a selected day in a bottom sheet/modal.
 * Used with CalendarView component.
 *
 * Issue #1059: Visual calendar view for club events
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar } from 'lucide-react';
import EventCard from './EventCard';

interface CalendarEvent {
  id: string;
  short_code: string;
  title: string;
  starts_at: number;
  location_name: string | null;
  hero_image_url: string | null;
  card_image_url?: string | null; // #1962: server-computed 480x240 derivative; optional because legacy callers may not pass it
  rsvp_count?: number;
  event_number?: number | null; // #1509
}

interface CalendarDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date | null;
  events: CalendarEvent[];
}

export default function CalendarDayModal({
  isOpen,
  onClose,
  date,
  events,
}: CalendarDayModalProps) {
  const navigate = useNavigate();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !date) return null;

  const dateLabel = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const handleEventClick = (event: CalendarEvent) => {
    navigate(`/event/${event.short_code || event.id}`);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-modal-title"
        className="
          bg-oro-surface border border-oro-border
          w-full sm:max-w-lg sm:rounded-xl
          rounded-t-xl
          max-h-[80vh] overflow-hidden
          shadow-card
          animate-in slide-in-from-bottom duration-200
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-oro-border sticky top-0 bg-oro-surface z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 id="day-modal-title" className="font-semibold text-text-primary">
                {dateLabel}
              </h2>
              <p className="text-sm text-text-muted">
                {events.length} {events.length === 1 ? 'event' : 'events'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        {/* Events list */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)] space-y-3">
          {events.length > 0 ? (
            events.map((event) => {
              const eventDate = new Date(event.starts_at * 1000);
              const formattedDate = eventDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              });
              const formattedTime = eventDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              });

              return (
                <EventCard
                  key={event.id}
                  event={{
                    id: event.id,
                    short_code: event.short_code,
                    title: event.title,
                    date: `${formattedDate} at ${formattedTime}`,
                    location: event.location_name || 'Location TBD',
                    attendees: event.rsvp_count || 0,
                    imageUrl: event.card_image_url || event.hero_image_url || '', // #1962 prefers card-sized variant
                    event_number: event.event_number ?? null,
                  }}
                  onClick={() => handleEventClick(event)}
                />
              );
            })
          ) : (
            <div className="py-12 text-center">
              <p className="text-text-muted">No events on this day</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
